/**
 * What's New Overlay
 * ──────────────────
 * Shows unseen release notes (src/data/whats-new.ts) once per version.
 * Mounted at the router level; renders only on the main tab routes, waits
 * until a streak celebration (which is rarer and more personal) is done,
 * and records the seen version in localStorage — device-level state, so it
 * intentionally survives sign-out and is NOT in USER_DATA_KEYS.
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { create } from 'zustand';
import { WHATS_NEW, LATEST_WHATS_NEW_VERSION } from '@/data/whats-new';
import { useAuthStore } from '@/lib/auth-store';
import { useStreakStore } from '@/lib/streak-store';
import { useTheme } from '@/lib/theme-context';
import { font } from '@/design-system/tokens';

const SEEN_KEY = 'kader-whatsnew-seen';
const MAIN_ROUTES = new Set(['/', '/plan', '/progress', '/coach', '/profile']);

function seenVersion(): number {
  try { return parseInt(localStorage.getItem(SEEN_KEY) ?? '0', 10) || 0; } catch { return 0; }
}

/** Manual reopen hook (e.g. a future "What's new" row in the Me tab). */
export const useWhatsNewStore = create<{ forcedOpen: boolean; open: () => void; close: () => void }>((set) => ({
  forcedOpen: false,
  open: () => set({ forcedOpen: true }),
  close: () => set({ forcedOpen: false }),
}));

function SparkleIcon({ size = 20, color }: { size?: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"/>
      <path d="M19 15l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"/>
    </svg>
  );
}

export default function WhatsNewOverlay() {
  const { colors } = useTheme();
  const location = useLocation();
  const user = useAuthStore(s => s.user);
  const pendingMilestone = useStreakStore(s => s.pendingMilestoneCelebration);
  const pendingLevelUp = useStreakStore(s => s.pendingLevelUp);
  const forcedOpen = useWhatsNewStore(s => s.forcedOpen);
  const closeForced = useWhatsNewStore(s => s.close);
  const [dismissed, setDismissed] = useState(false);

  const unseen = WHATS_NEW.filter(e => e.version > seenVersion());
  const autoShow = unseen.length > 0 && !dismissed;
  const entries = forcedOpen ? WHATS_NEW : unseen;

  if (!forcedOpen) {
    if (!autoShow) return null;
    if (!user) return null;                              // wait for sign-in
    if (!MAIN_ROUTES.has(location.pathname)) return null; // never over intake/login
    if (pendingMilestone !== null || pendingLevelUp !== null) return null; // celebrations first
  }

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, String(LATEST_WHATS_NEW_VERSION)); } catch { /* ignore */ }
    setDismissed(true);
    closeForced();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="What's new in Kader"
      style={{
        position: 'fixed', inset: 0, zIndex: 45, // below CelebrationOverlay (50)
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)',
      }}
      onClick={dismiss}
    >
      <style>{`
        @keyframes whatsnew-rise { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 430, maxHeight: '80dvh', overflowY: 'auto',
          background: colors.bgCard, borderRadius: '20px 20px 0 0',
          border: `1px solid ${colors.border}`, borderBottom: 'none',
          padding: '24px 24px 32px',
          animation: 'whatsnew-rise 0.3s ease-out',
        }}
        className="hide-scrollbar"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <SparkleIcon color={colors.accent} />
          <h2 style={{ fontSize: 19, fontWeight: 600, margin: 0, color: colors.textPrimary }}>What's new</h2>
        </div>

        {entries.map(entry => (
          <div key={entry.version} style={{ marginTop: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary }}>{entry.title}</span>
              <span style={{ fontSize: 10, color: colors.textMuted, fontFamily: font.mono }}>
                {new Date(entry.date + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {entry.items.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 10 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: colors.accent, marginTop: 6, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary }}>{item.title}</div>
                    <div style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.55, marginTop: 1 }}>{item.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <button onClick={dismiss} style={{
          width: '100%', marginTop: 24, padding: '13px 16px',
          background: colors.accent, border: 'none', borderRadius: 12,
          fontSize: 14, fontWeight: 600, color: colors.bgPrimary, cursor: 'pointer',
        }}>
          Got it
        </button>
      </div>
    </div>
  );
}
